<template>
  <div class="workbench-page">
    <WorkbenchSidebar
      :logged-in="loggedIn"
      :balance="quotaBalance"
      @recharge="handleRecharge"
      @login="goLogin" />

    <div class="workbench-body">
      <div class="workbench-scroll">
        <WorkbenchTopbar
          :logged-in="loggedIn"
          :nick-name="userInfo.nickName"
          :avatar="userInfo.avatar"
          :unread-count="unreadCount"
          @notify="handleNotify"
          @redeem="handleRedeemCode"
          @login="goLogin"
          @register="goRegister"
          @logout="handleLogout" />

        <main class="workbench-content" :class="{ 'workbench-content-task': isTaskRoute }">
          <div v-if="!loggedIn && !isTaskRoute" class="guest-banner">
            <i class="bi bi-info-circle"></i>
            <span>当前为访客模式，可浏览功能界面。登录后可提交处理、查看点数余额与历史记录。</span>
            <a href="#" class="guest-link" @click.prevent="goLogin">登录</a>
          </div>
          <router-view
              :readonly="!loggedIn"
              :balance="quotaBalance"
              @require-login="goLogin"
              @require-recharge="handleRecharge"
              @recharge-success="onRechargeSuccess"
              @process-submitted="refreshQuotaBalance"
              @process-success="onProcessSuccess" />
        </main>
      </div>
    </div>

    <RechargeModal
      :visible="showRecharge"
      :balance="quotaBalance"
      :user-name="displayName"
      :ledger-records="pointsLedger"
      @close="showRecharge = false"
      @open-redeem="handleRedeemCode"
      @recharge-success="onRechargeSuccess" />

    <RedeemCodeModal
      :visible="showRedeemCode"
      @close="showRedeemCode = false"
      @success="onRedeemSuccess" />

    <ArticleNotifyModal
      :visible="notifyVisible"
      @close="notifyVisible = false"
      @read-change="refreshUnreadCount" />

    <WorkbenchWelcomeModal
      :visible="welcomeVisible"
      :logged-in="loggedIn"
      :new-member-reward="welcomePromo.newMemberReward"
      :inviter-reward="welcomePromo.inviterReward"
      @close="closeWelcome"
      @login="onWelcomeLogin"
      @invite="onWelcomeInvite"
    />
  </div>
</template>

<script>
import WorkbenchSidebar from '@/components/workbench/WorkbenchSidebar.vue'
import WorkbenchTopbar from '@/components/workbench/WorkbenchTopbar.vue'
import RechargeModal from '@/components/recharge/RechargeModal.vue'
import RedeemCodeModal from '@/components/recharge/RedeemCodeModal.vue'
import { getProfile, getQuotaBalance, getQuotaLogs } from '@/api/portal/member'
import { logout } from '@/api/portal/auth'
import { isLoggedIn, getMember, setMember, clearAuth, getCachedQuotaBalance, setCachedQuotaBalance } from '@/utils/auth'

import portalAuthMixin from '@/mixins/portalAuthMixin'
import articleNotifyMixin from '@/mixins/articleNotifyMixin'
import workbenchWelcomeMixin from '@/mixins/workbenchWelcomeMixin'
import redeemCodeMixin from '@/mixins/redeemCodeMixin'

export default {
  name: 'WorkbenchView',
  mixins: [portalAuthMixin, articleNotifyMixin, workbenchWelcomeMixin, redeemCodeMixin],
  components: {
    WorkbenchSidebar,
    WorkbenchTopbar,
    RechargeModal,
    RedeemCodeModal
  },
  data() {
    const cachedBalance = getCachedQuotaBalance()
    return {
      loggedIn: isLoggedIn(),
      quotaBalance: cachedBalance != null ? cachedBalance : 0,
      showRecharge: false,
      pointsLedger: [],
      userInfo: getMember() || { nickName: '访客' }
    }
  },
  computed: {
    displayName() {
      return this.userInfo.nickName || this.userInfo.userName || '用户'
    },
    isTaskRoute() {
      return this.$route.name === 'process-task'
    }
  },
  mounted() {
    if (this.loggedIn) {
      this.loadUserData()
    }
  },
  methods: {
    loadUserData() {
      getProfile()
        .then(res => {
          this.userInfo = res.data
          setMember(res.data)
        })
        .catch(() => {
          clearAuth()
          this.loggedIn = false
          this.userInfo = { nickName: '访客' }
        })
      getQuotaBalance()
        .then(res => {
          const data = res.data || {}
          this.quotaBalance = data.available != null ? data.available : (data.balance || 0)
          setCachedQuotaBalance(this.quotaBalance)
        })
        .catch(() => {
          if (getCachedQuotaBalance() == null) {
            this.quotaBalance = 0
          }
        })
    },
    loadPointsLedger() {
      return getQuotaLogs()
        .then(res => {
          this.pointsLedger = res.data || []
        })
        .catch(() => {
          this.pointsLedger = []
        })
    },
    handleLogout() {
      logout().finally(() => {
        clearAuth()
        this.loggedIn = false
        this.userInfo = { nickName: '访客' }
        this.quotaBalance = 0
      })
    },
    handleRecharge() {
      if (!this.loggedIn) {
        this.goLogin()
        return
      }
      this.loadPointsLedger()
      this.showRecharge = true
    },
    onRechargeSuccess(result) {
      this.quotaBalance = result.available != null ? result.available : (result.balance || 0)
      setCachedQuotaBalance(this.quotaBalance)
      this.loadPointsLedger()
    },
    onProcessSuccess() {
      this.refreshQuotaBalance()
    },
    refreshQuotaBalance() {
      return getQuotaBalance()
        .then(res => {
          const data = res.data || {}
          this.quotaBalance = data.available != null ? data.available : (data.balance || 0)
          setCachedQuotaBalance(this.quotaBalance)
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.workbench-page {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #f6f7fb;
}

.workbench-body {
  flex: 1;
  min-width: 0;
  height: 100vh;
  overflow: hidden;
}

.workbench-scroll {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.workbench-content {
  padding: 0 28px 40px;
}

.workbench-content-task {
  padding-bottom: 24px;
}

.guest-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 14px;
}

.guest-banner i {
  font-size: 18px;
}

.guest-link {
  color: var(--primary);
  font-weight: 500;
  white-space: nowrap;
}
</style>
